import type { UserInformation } from '@/common/api/models.pleased';
import type { DraftUser } from '@/stores/useBotUser';
import { useBotUser } from '@/stores/useBotUser';
import { store as vuexStore } from '@/store';
import { PleasedClient } from '@/pleased-sdk/PleasedClient';
import { devLog } from '@/common/debug';

/**
 * Create mocked authenticated user data for preview/mocked mode.
 * This mimics the response from PleasedApi.getPleasedTokenFromSportyToken.
 */
export const createMockedUserData = (): UserInformation => ({
  id: 'mocked-user-123',
  foreignId: 'mocked-foreign-id',
  name: 'Preview User',
  phone: '+1234567890',
  email: 'preview@example.com',
  token: 'mocked-token-preview-mode',
  greyListed: false,
  defaultName: 'Preview User',
  userLoyaltyTierBand: 'Gold',
  createdAt: new Date(),
  updatedAt: new Date(),
  roleId: 1,
  sourceId: 1,
  isActive: true,
  extraFields: [],
});

/**
 * Create mocked guest user data for preview/mocked mode.
 * This sets up a guest user without authentication.
 */
export const createMockedGuestData = (): DraftUser => ({
  name: 'Guest Preview',
  email: 'guest@example.com',
  phone: '+9876543210',
  defaultName: 'Guest User',
  foreignId: 'guest-foreign-id',
  userLoyaltyTierBand: '',
});

/**
 * Set the current user in the Pinia store.
 * This ensures the useBotUser store is updated alongside Vuex.
 *
 * @param user - The user information to set in the store
 */
export const setCurrentUserInPinia = (user: UserInformation) => {
  const userStore = useBotUser();
  userStore.setCurrentUser(user);
};

/**
 * Set the guest user in the Pinia store.
 * This ensures the useBotUser store is updated for guest flows.
 *
 * @param guestData - The guest user data to set in the store
 */
export const setGuestUserInPinia = (guestData: DraftUser) => {
  const userStore = useBotUser();
  userStore.setGuestUser(guestData);
  userStore.setCurrentUser(null as any);
};

/**
 * Mock the user login process by setting mocked user data in both stores.
 * This bypasses the API call and sets up the stores as if login was successful.
 * Mimics the behavior of loginViaToken in useBotUser.ts.
 */
export const mockUserLogin = () => {
  const mockedUser = createMockedUserData();

  // Update Vuex store (same as what loginViaToken does)
  vuexStore.commit('user/updatePleasedUser', mockedUser);
  vuexStore.commit('user/updateUserGreyListed', { greyListed: mockedUser.greyListed });

  // Update Pinia store (same as what loginViaToken does)
  setCurrentUserInPinia(mockedUser);

  // Update PleasedClient config with mocked token (same as loginViaToken)
  PleasedClient.setConfig({ token: mockedUser.token });

  devLog.debug('[MOCKED_USER] User login mocked', mockedUser);
};

/**
 * Mock a guest user by setting up guest user data without authentication.
 * This is used for guest form flows where the user is not logged in.
 */
export const mockGuestUser = () => {
  const mockedGuest = createMockedGuestData();

  // Update Pinia store - set guest user and clear authenticated user
  setGuestUserInPinia(mockedGuest);

  // Don't set token for guest users
  PleasedClient.setConfig({ token: null });

  devLog.debug('[MOCKED_USER] Guest user mocked', mockedGuest);
};
